import { PropsWithChildren, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { ThemeColours } from '../../styles/ThemeColours';
import { FontWeight, FONT_STACK, PRESSABLE } from '../../styles/constants';
import { ButtonDesignStyle } from './ButtonDesignStyles';
import { ButtonVariants, Variant } from './DeprecatedButtonVariants';

export const getBackgroundColor = ({
  options,
  variant = ButtonVariants.primary,
  buttonDesignStyle,
}: ButtonStyleProps) => {
  if (options?.textOnly) {
    return css`
      background-color: transparent;
    `;
  } else if (buttonDesignStyle) {
    return css`
      background-color: ${buttonDesignStyle.backgroundColour};

      &:hover {
        background-color: ${buttonDesignStyle.hover.backgroundColour};
      }
      &:active {
        background-color: ${buttonDesignStyle.active.backgroundColour};
      }
      &:disabled {
        background-color: ${buttonDesignStyle.disabled.backgroundColour};
      }
    `;
  }

  // Support for the deprecated button 'keyline' value
  if (options?.keyline) {
    return css`
      background-color: ${ThemeColours.white};
    `;
  }

  // When the deprecated 'variant' type gets removed, we can then make
  // ButtonDesignStyle a required type and remove this default ending return statement
  return css`
    background-color: ${variant.primary};
  `;
};

export const getBorderStyle = ({ buttonDesignStyle, options }: ButtonStyleProps) => {
  if (buttonDesignStyle) {
    return css`
      border: 2px solid ${buttonDesignStyle.border};

      &:hover {
        border: 2px solid ${buttonDesignStyle.hover.border};
      }
      &:active {
        border: 2px solid ${buttonDesignStyle.active.border};
      }
      &:disabled {
        border: 2px solid ${buttonDesignStyle.disabled.border};
      }
    `;
  } else {
    return css`
      border: ${options?.keyline ? css`2px solid ${ThemeColours.mono200}` : '0'};
    `;
  }
};

export const getSizing = ({ options }: ButtonStyleProps) => css`
  ${options?.textOnly
    ? css`
        display: inline-flex;
        font-weight: ${FontWeight.REGULAR};
        padding: 0px;
      `
    : css`
        border-radius: 24px;
        display: flex;
        font-weight: ${FontWeight.SEMI_BOLD};
        height: ${options?.small ? '36px' : '48px'};
        padding: 0px 16px;
        width: 100%;
      `}
`;

export const getTextColor = ({
  options,
  variant = ButtonVariants.primary,
  buttonDesignStyle,
}: ButtonStyleProps) => {
  if (buttonDesignStyle) {
    if (options?.textOnly) {
      return css`
        color: ${buttonDesignStyle.backgroundColour};
      `;
    } else {
      return css`
        color: ${buttonDesignStyle?.fontColour};

        :hover {
          color: ${buttonDesignStyle?.hover.fontColour};
        }
        :active {
          color: ${buttonDesignStyle?.active.fontColour};
        }
        :disabled {
          color: ${buttonDesignStyle?.disabled.fontColour};
        }
      `;
    }
  } else {
    if (options?.keyline || options?.textOnly) {
      return css`
        color: ${variant.primary};
      `;
    }

    // When the deprecated 'variant' type gets removed, we can then make
    // ButtonDesignStyle a required type and remove this default ending return statement
    return css`
      color: ${variant.secondary};
    `;
  }
};

export const constantButtonStyles = ({ buttonDesignStyle }: ButtonStyleProps) => css`
  ${FONT_STACK};
  ${PRESSABLE};

  align-items: center;
  box-sizing: border-box;
  flex-direction: row;
  font-size: 14px;
  justify-content: center;
  text-decoration: none;
  white-space: nowrap;

  &[disabled] {
    opacity: ${buttonDesignStyle ? '1' : '0.5'};
    pointer-events: none;
  }
`;

/** @TODO not yet implement as per the design language */
enum ButtonSize {
  MINI,
  COMPACT,
  DEFAULT,
  LARGE,
}

/** @deprecated use ButtonSize */
type Small = {
  small?: boolean;
};

/** @deprecated use an appropriate button design from ButtonDesignStyles */
type Keyline = {
  keyline?: boolean;
};

type Options = Small &
  Keyline & {
    buttonSize?: ButtonSize;
    textOnly?: boolean;
    disabled?: boolean;
  };

/** @deprecated replaced with ButtonDesignStyle */
type DeprecatedVariant = {
  variant?: Variant;
};

export type ButtonStyleProps = DeprecatedVariant & {
  buttonDesignStyle?: ButtonDesignStyle;
  options?: Options;
};

export type ButtonContentProps = PropsWithChildren<{
  leftContent?: ReactNode;
  rightContent?: ReactNode;
}>;

export const MiddleCompartment = styled.div<ButtonStyleProps & ButtonContentProps>`
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  justify-content: center;
  padding-inline-end: ${props => (!props.options?.textOnly || props.rightContent ? '8px' : '0')};
  padding-inline-start: ${props => (!props.options?.textOnly || props.leftContent ? '8px' : '0')};
`;
